import { MAX_LEVEL } from '@root/helpers/schema/schema-cdr/patterns/scheduleItem';

import type { ScheduleItemValue, SchedulePath } from './types';

export const pathKey = (path: SchedulePath): string => path.join('-');

export const getLevel = (path: SchedulePath): number => path.length;

export const canAddChild = (path: SchedulePath): boolean => getLevel(path) < MAX_LEVEL;

export const getNumberingLabel = (path: SchedulePath): string => path.map((index) => index + 1).join('.');

const createEmptyItem = (level: number): ScheduleItemValue => (level < MAX_LEVEL ? { children: [] } : {});

export const addTopLevelItem = (tree: ScheduleItemValue[]): ScheduleItemValue[] => [...tree, createEmptyItem(1)];

/** Replaces the siblings array that directly contains `path`'s last index with whatever `fn` returns. */
const updateSiblingsAtPath = (
  tree: ScheduleItemValue[],
  path: SchedulePath,
  fn: (siblings: ScheduleItemValue[], index: number) => ScheduleItemValue[],
): ScheduleItemValue[] => {
  const [index, ...rest] = path;

  if (rest.length === 0) {
    return fn(tree, index);
  }

  return tree.map((item, itemIndex) => {
    if (itemIndex !== index) return item;
    return { ...item, children: updateSiblingsAtPath(item.children ?? [], rest, fn) };
  });
};

export const updateItemAtPath = (
  tree: ScheduleItemValue[],
  path: SchedulePath,
  patch: Partial<ScheduleItemValue>,
): ScheduleItemValue[] =>
  updateSiblingsAtPath(tree, path, (siblings, index) =>
    siblings.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
  );

export const addChildAtPath = (tree: ScheduleItemValue[], path: SchedulePath): ScheduleItemValue[] =>
  updateSiblingsAtPath(tree, path, (siblings, index) =>
    siblings.map((item, itemIndex) =>
      itemIndex === index
        ? // The child lands one level below its parent, so that is the depth it is built for.
          { ...item, children: [...(item.children ?? []), createEmptyItem(getLevel(path) + 1)] }
        : item,
    ),
  );

/** Removes the item at `path`; its own children are promoted into its place, keeping order. */
export const removeItemAtPath = (tree: ScheduleItemValue[], path: SchedulePath): ScheduleItemValue[] =>
  updateSiblingsAtPath(tree, path, (siblings, index) => {
    const target = siblings[index];
    const promoted = target?.children ?? [];
    return [...siblings.slice(0, index), ...promoted, ...siblings.slice(index + 1)];
  });

export const moveItemAtPath = (tree: ScheduleItemValue[], path: SchedulePath, direction: -1 | 1): ScheduleItemValue[] =>
  updateSiblingsAtPath(tree, path, (siblings, index) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= siblings.length) return siblings;

    const next = [...siblings];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next;
  });

const parseKey = (key: string): SchedulePath => key.split('-').map(Number);

const startsWith = (path: SchedulePath, prefix: SchedulePath): boolean =>
  path.length >= prefix.length && prefix.every((segment, i) => path[i] === segment);

/**
 * Expanded state is keyed by position, so any operation that renumbers siblings would otherwise
 * hand one item's expanded state to whichever item took its index. These remap the keys the same
 * way the operation above renumbers the tree.
 */
export const remapKeysForMove = (keys: Set<string>, path: SchedulePath, direction: -1 | 1): Set<string> => {
  const parent = path.slice(0, -1);
  const from = path[path.length - 1];
  const to = from + direction;

  return new Set(
    [...keys].map((key) => {
      const parsed = parseKey(key);
      if (!startsWith(parsed, parent) || parsed.length <= parent.length) return key;

      // Only the two swapped subtrees move; everything else keeps its index.
      const index = parsed[parent.length];
      if (index !== from && index !== to) return key;

      const next = [...parsed];
      next[parent.length] = index === from ? to : from;
      return pathKey(next);
    }),
  );
};

export const remapKeysForRemove = (keys: Set<string>, path: SchedulePath, promotedCount: number): Set<string> => {
  const parent = path.slice(0, -1);
  const removed = path[path.length - 1];
  const remapped = new Set<string>();

  for (const key of keys) {
    const parsed = parseKey(key);

    if (!startsWith(parsed, parent) || parsed.length <= parent.length) {
      remapped.add(key);
      continue;
    }

    const index = parsed[parent.length];

    // The removed row itself has no expanded state left to keep.
    if (index === removed && parsed.length === parent.length + 1) continue;

    if (index === removed) {
      // Its children are promoted into its place, so each child's index becomes removed + child.
      const childIndex = parsed[parent.length + 1];
      const next = [...parent, removed + childIndex, ...parsed.slice(parent.length + 2)];
      remapped.add(pathKey(next));
      continue;
    }

    if (index > removed) {
      // Later siblings shift by however many children took the removed row's single slot.
      const next = [...parsed];
      next[parent.length] = index + promotedCount - 1;
      remapped.add(pathKey(next));
      continue;
    }

    remapped.add(key);
  }

  return remapped;
};

/**
 * Rebuilds the `ownKey` the validator reports for the item at `path`, so a row can find its own
 * errors. Validation keys use bracket notation for indices (see src/formatErrors.ts), e.g.
 * `risk_details.limits_schedule[0].entries[1].children[0]`.
 */
export const schedulePathToOwnKey = (ownKey: string, path: SchedulePath): string =>
  path.reduce((acc, index, depth) => (depth === 0 ? `${acc}[${index}]` : `${acc}.children[${index}]`), ownKey);

/** Number of items in the array that directly contains `path` — i.e. how many siblings it has. */
export const countSiblingsAtPath = (tree: ScheduleItemValue[], path: SchedulePath): number => {
  let items: ScheduleItemValue[] = tree;

  for (const index of path.slice(0, -1)) {
    items = items[index]?.children ?? [];
  }

  return items.length;
};

/** Number of children the item at `path` holds — what `removeItemAtPath` promotes in its place. */
export const countChildrenAtPath = (tree: ScheduleItemValue[], path: SchedulePath): number => {
  let items: ScheduleItemValue[] | undefined = tree;
  let item: ScheduleItemValue | undefined;

  for (const index of path) {
    item = items?.[index];
    items = item?.children;
  }

  return item?.children?.length ?? 0;
};

export const collectAllPathKeys = (tree: ScheduleItemValue[], prefix: SchedulePath = []): string[] =>
  tree.flatMap((item, index) => {
    const path = [...prefix, index];
    return [pathKey(path), ...collectAllPathKeys(item.children ?? [], path)];
  });
